// M10 – Thống kê giao dịch (6.10): KPI so kỳ trước, doanh thu theo thời gian / phương thức / chi nhánh, top hàng, nhân viên, hiệu quả KM
import { useMemo, useState, type ReactNode } from 'react';
import { DatePicker, Select, Switch } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowDownRight, ArrowUpRight, Download } from 'lucide-react';
import { BRANCHES, METHOD_LABEL, METHODS, staffName, type Order, type PaymentMethod } from './data';
import usePosStore from './store';
import { BRAND, branchOptions, exportExcel, money, moneyShort, usePosAuth } from './utils';
import PosShell from './components/PosShell';

// Màu phân loại theo thứ tự cố định (bảng màu tham chiếu đã kiểm tra mù màu) — gắn theo phương thức, không theo thứ hạng
const METHOD_COLOR: Record<PaymentMethod, string> = { cash: '#2a78d6', transfer: '#eb6834', card: '#1baf7a', ewallet: '#eda100', prepaid: '#e87ba4' };
const PREV_COLOR = '#9ca3af';
const COUNTED: Order['status'][] = ['completed', 'debt', 'partially_refunded'];

const PRESETS: { label: string; value: [Dayjs, Dayjs] }[] = [
  { label: 'Hôm nay', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
  { label: '7 ngày', value: [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')] },
  { label: 'Tháng này', value: [dayjs().startOf('month'), dayjs().endOf('day')] },
  { label: 'Tháng trước', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { label: '30 ngày', value: [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')] },
];

export default function PosDashboard() {
  const navigate = useNavigate();
  const orders = usePosStore(s => s.orders);
  const transactions = usePosStore(s => s.transactions);
  const refunds = usePosStore(s => s.refunds);
  const workingBranch = usePosStore(s => s.workingBranch);
  const { can, role } = usePosAuth();

  const [range, setRange] = useState<[Dayjs, Dayjs]>(PRESETS[2].value);
  const [branches, setBranches] = useState<string[]>(role === 'cashier' ? [workingBranch] : []);
  const [source, setSource] = useState<'all' | 'pos' | 'myspa'>('all');
  const [compare, setCompare] = useState(true);

  const days = range[1].diff(range[0], 'day') + 1;
  const prev: [Dayjs, Dayjs] = [range[0].subtract(days, 'day'), range[0].subtract(1, 'millisecond')];
  // S1: ≤ 2 ngày theo giờ, ≤ 92 ngày theo ngày, dài hơn theo tháng
  const unit: 'hour' | 'day' | 'month' = days <= 2 ? 'hour' : days <= 92 ? 'day' : 'month';
  const bucketFmt = unit === 'hour' ? 'DD/MM HH:00' : unit === 'day' ? 'DD/MM' : 'MM/YYYY';

  const inBranch = (b: string) => !branches.length || branches.includes(b);
  const orderById = useMemo(() => new Map(orders.map(o => [o.id, o])), [orders]);

  /** Dòng tiền trong kỳ: giao dịch POS theo ngày thu + đơn MySpa theo "Đã thanh toán" (BR10) */
  const cashflow = (from: Dayjs, to: Dayjs) => {
    const items: { at: Dayjs; amount: number; method: PaymentMethod | null; branchId: string }[] = [];
    if (source !== 'myspa') {
      transactions.forEach(t => {
        const at = dayjs(t.at);
        if (at.isBefore(from) || at.isAfter(to) || !inBranch(t.branchId)) return;
        if (orderById.get(t.orderId)?.source === 'myspa') return;
        items.push({ at, amount: t.amount, method: t.method, branchId: t.branchId });
      });
    }
    if (source !== 'pos') {
      orders.forEach(o => {
        if (o.source !== 'myspa' || o.status === 'cancelled' || !inBranch(o.branchId)) return;
        const at = dayjs(o.createdAt);
        if (!at.isBefore(from) && !at.isAfter(to)) items.push({ at, amount: o.paid, method: null, branchId: o.branchId });
      });
    }
    return items;
  };

  const periodOrders = (from: Dayjs, to: Dayjs) => orders.filter(o => {
    const at = dayjs(o.createdAt);
    return !at.isBefore(from) && !at.isAfter(to) && inBranch(o.branchId) && (source === 'all' || o.source === source) && COUNTED.includes(o.status);
  });

  const kpi = (from: Dayjs, to: Dayjs) => {
    const flow = cashflow(from, to);
    const os = periodOrders(from, to);
    const revenue = flow.reduce((s, x) => s + x.amount, 0);
    const refunded = refunds.filter(r => r.status === 'paid' && r.paidAt && !dayjs(r.paidAt).isBefore(from) && !dayjs(r.paidAt).isAfter(to) && inBranch(r.branchId)).reduce((s, r) => s + r.amount, 0);
    const discount = os.reduce((s, o) => s + o.totals.itemPromo + o.totals.lineDiscount + o.totals.orderPromo + o.totals.orderDiscount + o.totals.voucher, 0);
    return { revenue, count: os.length, avg: os.length ? revenue / os.length : 0, discount, refunded };
  };

  const cur = useMemo(() => kpi(range[0], range[1]), // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, branches, source, orders, transactions, refunds]);
  const before = useMemo(() => kpi(prev[0], prev[1]), // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, branches, source, orders, transactions, refunds]);
  const debtNow = orders.filter(o => o.status === 'debt' && inBranch(o.branchId) && (source === 'all' || o.source === source));
  const debtTotal = debtNow.reduce((s, o) => s + o.debt, 0);

  const series = useMemo(() => {
    const map = new Map<string, { key: string; label: string; cur: number; prev: number; date: Dayjs }>();
    let cursor = range[0].startOf(unit);
    while (!cursor.isAfter(range[1])) {
      map.set(cursor.format('YYYY-MM-DD HH'), { key: cursor.format('YYYY-MM-DD HH'), label: cursor.format(bucketFmt), cur: 0, prev: 0, date: cursor });
      cursor = cursor.add(1, unit);
    }
    cashflow(range[0], range[1]).forEach(x => { const b = map.get(x.at.startOf(unit).format('YYYY-MM-DD HH')); if (b) b.cur += x.amount; });
    if (compare) cashflow(prev[0], prev[1]).forEach(x => {
      const shifted = x.at.add(days, 'day').startOf(unit).format('YYYY-MM-DD HH');
      const b = map.get(shifted); if (b) b.prev += x.amount;
    });
    return Array.from(map.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, branches, source, compare, orders, transactions, unit]);

  const methodMix = useMemo(() => {
    const sums = new Map<PaymentMethod, number>();
    cashflow(range[0], range[1]).forEach(x => { if (x.method && x.amount > 0) sums.set(x.method, (sums.get(x.method) || 0) + x.amount); });
    const total = Array.from(sums.values()).reduce((s, v) => s + v, 0);
    return METHODS.filter(m => sums.get(m)).map(m => ({ method: m, name: METHOD_LABEL[m], value: sums.get(m)!, pct: total ? (sums.get(m)! / total) * 100 : 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, branches, source, transactions, orders]);

  const byBranch = useMemo(() => BRANCHES.filter(b => inBranch(b.id)).map(b => ({
    id: b.id, name: b.name.replace('Khothi ', ''), value: cashflow(range[0], range[1]).filter(x => x.branchId === b.id).reduce((s, x) => s + x.amount, 0),
  })).sort((a, b) => b.value - a.value),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [range, branches, source, transactions, orders]);

  const os = useMemo(() => periodOrders(range[0], range[1]), // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, branches, source, orders]);
  const topItems = useMemo(() => {
    const m = new Map<string, { name: string; qty: number; net: number }>();
    os.forEach(o => o.lines.forEach(l => {
      const k = l.itemId || l.name;
      const e = m.get(k) || { name: l.name, qty: 0, net: 0 };
      e.qty += l.qty - l.refundedQty; e.net += l.net - l.refundedAmount;
      m.set(k, e);
    }));
    return Array.from(m.values()).sort((a, b) => b.net - a.net).slice(0, 10);
  }, [os]);
  const byStaff = useMemo(() => {
    const m = new Map<string, { name: string; orders: Set<string>; net: number }>();
    os.forEach(o => o.lines.forEach(l => {
      const name = staffName(l.staffId) || 'Chưa gán';
      const e = m.get(name) || { name, orders: new Set<string>(), net: 0 };
      e.orders.add(o.id); e.net += l.net - l.refundedAmount;
      m.set(name, e);
    }));
    return Array.from(m.values()).sort((a, b) => b.net - a.net);
  }, [os]);
  const promoEffect = useMemo(() => {
    const m = new Map<string, { name: string; count: number; amount: number }>();
    const add = (name: string, amount: number) => { if (!amount) return; const e = m.get(name) || { name, count: 0, amount: 0 }; e.count += 1; e.amount += amount; m.set(name, e); };
    os.forEach(o => {
      o.lines.forEach(l => add(l.promoName || 'KM theo dòng', l.promoAmount));
      add(o.totals.orderPromoName || 'KM theo đơn', o.totals.orderPromo);
      add(`Voucher ${o.voucherCode}`, o.totals.voucher);
      add('Chiết khấu thủ công', o.totals.lineDiscount + o.totals.orderDiscount);
    });
    return Array.from(m.values()).sort((a, b) => b.amount - a.amount);
  }, [os]);

  // S2: bấm cột / dòng → mở M3 với bộ lọc tương ứng
  const goOrders = (from: Dayjs, to: Dayjs, branch?: string) => {
    const p = new URLSearchParams({ from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') });
    const b = branch ? [branch] : branches;
    if (b.length) p.set('branch', b.join(','));
    if (source !== 'all') p.set('source', source);
    navigate(`/pos/orders?${p.toString()}`);
  };

  const doExport = () => exportExcel(`Thong_ke_POS_${range[0].format('YYYYMMDD')}_${range[1].format('YYYYMMDD')}.xlsx`, 'Doanh thu', [
    ...series.map(s => ({ 'Thời gian': s.label, 'Doanh thu thuần': s.cur, 'Kỳ trước': s.prev })),
    { 'Thời gian': 'TỔNG', 'Doanh thu thuần': cur.revenue, 'Kỳ trước': before.revenue },
  ]);

  const presetLabel = PRESETS.find(p => p.value[0].isSame(range[0], 'day') && p.value[1].isSame(range[1], 'day'))?.label;

  return (
    <PosShell
      title="Thống kê bán hàng"
      subtitle={`${presetLabel || `${range[0].format('DD/MM/YYYY')} – ${range[1].format('DD/MM/YYYY')}`} · so với ${prev[0].format('DD/MM')} – ${prev[1].format('DD/MM/YYYY')}`}
      actions={can('export') && <button onClick={doExport} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Download className="h-4 w-4" /> Xuất Excel</button>}
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <DatePicker.RangePicker value={range} format="DD/MM/YYYY" allowClear={false} presets={PRESETS}
          onChange={v => v && v[0] && v[1] && setRange([v[0].startOf('day'), v[1].endOf('day')])} />
        <Select mode="multiple" allowClear placeholder="Tất cả chi nhánh" className="min-w-[180px]" maxTagCount="responsive" value={branches} onChange={setBranches} options={branchOptions} disabled={role === 'cashier'} />
        <Select className="w-36" value={source} onChange={setSource} options={[{ value: 'all', label: 'Nguồn: Tất cả' }, { value: 'pos', label: 'Nguồn: POS' }, { value: 'myspa', label: 'Nguồn: MySpa' }]} />
        <label className="flex items-center gap-2 text-sm text-slate-600"><Switch size="small" checked={compare} onChange={setCompare} /> So kỳ trước</label>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Kpi label="Doanh thu thuần" value={moneyShort(cur.revenue)} cur={cur.revenue} prev={before.revenue} />
        <Kpi label="Số đơn" value={String(cur.count)} cur={cur.count} prev={before.count} />
        <Kpi label="Trung bình / đơn" value={moneyShort(cur.avg)} cur={cur.avg} prev={before.avg} />
        <Kpi label="Tổng giảm giá" value={moneyShort(cur.discount)} cur={cur.discount} prev={before.discount} upIsBad />
        <Kpi label="Hoàn tiền" value={moneyShort(cur.refunded)} cur={cur.refunded} prev={before.refunded} upIsBad />
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-medium text-slate-500">Công nợ hiện tại</div>
          <div className="mt-1 text-2xl font-extrabold text-amber-600">{moneyShort(debtTotal)}</div>
          <button onClick={() => navigate('/pos/orders?tab=debt&from=2000-01-01')} className="text-xs text-[#2c6b7f] hover:underline">{debtNow.length} đơn còn nợ ›</button>
        </div>
      </div>

      <div className="mb-4 grid gap-4 xl:grid-cols-3">
        <Card title="Doanh thu theo thời gian" className="xl:col-span-2"
          legend={compare && <span className="flex items-center gap-3 text-xs text-slate-500"><Swatch color={BRAND.primary} /> Kỳ này <Swatch color={PREV_COLOR} dashed /> Kỳ trước</span>}>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#eef0f2" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} minTickGap={16} />
              <YAxis tickFormatter={v => moneyShort(v)} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={64} />
              <Tooltip cursor={{ fill: 'rgba(62,145,171,0.08)' }} formatter={(v: number, k: string) => [`${money(v)}đ`, k === 'cur' ? 'Kỳ này' : 'Kỳ trước']} labelStyle={{ fontWeight: 600 }} />
              <Bar dataKey="cur" fill={BRAND.primary} radius={[4, 4, 0, 0]} maxBarSize={28} cursor="pointer"
                onClick={(d: any) => d?.date && goOrders(d.date.startOf(unit === 'hour' ? 'day' : unit), d.date.endOf(unit === 'hour' ? 'day' : unit))} />
              {compare && <Line dataKey="prev" stroke={PREV_COLOR} strokeWidth={2} strokeDasharray="5 4" dot={false} />}
            </ComposedChart>
          </ResponsiveContainer>
          <p className="mt-1 text-xs text-slate-400">Bấm vào cột để xem danh sách đơn của mốc thời gian đó.</p>
        </Card>

        <Card title="Cơ cấu phương thức thanh toán">
          {methodMix.length === 0 ? <EmptyChart /> : (
            <div className="flex flex-col items-center gap-3 sm:flex-row xl:flex-col 2xl:flex-row">
              <ResponsiveContainer width={180} height={180}>
                <PieChart>
                  <Pie data={methodMix} dataKey="value" nameKey="name" innerRadius={52} outerRadius={84} paddingAngle={1} stroke="#fff" strokeWidth={2}>
                    {methodMix.map(m => <Cell key={m.method} fill={METHOD_COLOR[m.method]} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => `${money(v)}đ`} />
                </PieChart>
              </ResponsiveContainer>
              <ul className="w-full space-y-1.5 text-sm">
                {methodMix.map(m => (
                  <li key={m.method} className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-slate-700"><Swatch color={METHOD_COLOR[m.method]} /> {m.name}</span>
                    <span className="text-slate-500"><b className="text-slate-800">{m.pct.toFixed(0)}%</b> · {moneyShort(m.value)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card title="Doanh thu theo chi nhánh">
          {byBranch.every(b => !b.value) ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={Math.max(120, byBranch.length * 48)}>
              <BarChart data={byBranch} layout="vertical" margin={{ top: 0, right: 56, left: 0, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 12, fill: '#374151' }} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: 'rgba(62,145,171,0.08)' }} formatter={(v: number) => [`${money(v)}đ`, 'Doanh thu thuần']} />
                <Bar dataKey="value" fill={BRAND.primary} radius={[0, 4, 4, 0]} barSize={22} cursor="pointer"
                  label={{ position: 'right', formatter: (v: number) => moneyShort(v), fontSize: 12, fill: '#374151' }}
                  onClick={(d: any) => goOrders(range[0], range[1], d.id)} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Top 10 dịch vụ / sản phẩm">
          <MiniTable head={['Tên', 'SL', 'Doanh thu']} rows={topItems.map(t => [t.name, t.qty, moneyShort(t.net)])} />
        </Card>

        <Card title="Theo tư vấn / KTV">
          <MiniTable head={['Nhân viên', 'Số đơn', 'Doanh thu']} rows={byStaff.map(s => [s.name, s.orders.size, moneyShort(s.net)])} />
        </Card>

        <Card title="Hiệu quả khuyến mãi & chiết khấu" className="xl:col-span-3">
          <MiniTable head={['Nguồn giảm giá', 'Số lần áp', 'Tổng giảm', 'Tỷ trọng']}
            rows={promoEffect.map(p => [p.name, p.count, money(p.amount), `${cur.discount ? ((p.amount / cur.discount) * 100).toFixed(1) : 0}%`])} />
        </Card>
      </div>

      <p className="mt-4 text-xs text-slate-500">* Doanh thu tính theo ngày thu tiền (giao dịch Thu − Hoàn); đơn MySpa tính theo “Đã thanh toán” trong file. Số đơn tính theo ngày tạo đơn, gồm Hoàn thành, Còn nợ, Hoàn một phần.</p>
    </PosShell>
  );
}

function Kpi({ label, value, cur, prev, upIsBad }: { label: string; value: string; cur: number; prev: number; upIsBad?: boolean }) {
  const pct = prev ? ((cur - prev) / Math.abs(prev)) * 100 : null;
  const up = (pct || 0) >= 0;
  const good = upIsBad ? !up : up;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-extrabold text-slate-800">{value}</div>
      {pct === null ? <div className="text-xs text-slate-400">Kỳ trước chưa có dữ liệu</div> : (
        <div className={`flex items-center gap-0.5 text-xs font-semibold ${good ? 'text-green-700' : 'text-red-600'}`}>
          {up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
          {Math.abs(pct).toFixed(0)}% so kỳ trước
        </div>
      )}
    </div>
  );
}

const Card = ({ title, legend, className = '', children }: { title: string; legend?: ReactNode; className?: string; children: ReactNode }) => (
  <section className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`}>
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2"><h3 className="text-sm font-bold text-slate-800">{title}</h3>{legend}</div>
    {children}
  </section>
);
const Swatch = ({ color, dashed }: { color: string; dashed?: boolean }) => (
  dashed ? <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: color }} /> : <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
);
const EmptyChart = () => <div className="py-12 text-center text-sm text-slate-400">Chưa có dữ liệu trong kỳ</div>;
const MiniTable = ({ head, rows }: { head: string[]; rows: (string | number)[][] }) => rows.length === 0 ? <EmptyChart /> : (
  <table className="w-full text-sm">
    <thead className="text-xs text-slate-500"><tr>{head.map((h, i) => <th key={h} className={`pb-2 ${i ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr></thead>
    <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-slate-100">{r.map((c, j) => <td key={j} className={`py-1.5 ${j ? 'text-right' : 'text-left'} ${j === r.length - 1 && j > 0 ? 'font-semibold' : ''}`}>{c}</td>)}</tr>)}</tbody>
  </table>
);
